import React, { useCallback, useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import * as yup from "yup";
import HeadingSection from "../UI/HeadingSection";
import { Form, Formik } from "formik";
import { Field, FormButtonCluster } from "../UI/forms";
import * as r from "../../requests/groupManagementRequests";
import AlertContext from "../../context/alert/AlertContext";
import Button from "../UI/Button";
import { Body } from "../UI/Text";
import { Flex } from "reflexbox";
import { getUsers, updateUser } from "../../requests/userManagementRequests";
import styled, { useTheme } from "styled-components";
import { getGroup } from "../../requests/groupManagementRequests";
import { getPlatforms } from "../../requests/platformManagementRequests";
import { useQuery } from "../Hooks";
import { PageWrapper } from "../UI/Grid";
import { getCustomers } from "../../requests/customerManagementRequests";
import Table from "../UI/Table";
import Indicator from "../UI/Indicator";
import UserContext from "../../context/user/UserContext";
import { getProductLists } from "../../requests/productListManagementRequests";

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const validationSchema = yup.object().shape({
  pid: yup
    .string("Please select a platform to assign this group to.")
    .required("Platform is required!"),
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  defaultCustomer: yup.string().nullable(),
  productList: yup.number().nullable(),
});

const GroupForm = () => {
  const navigate = useNavigate();
  const { id: groupId } = useParams();
  const theme = useTheme();
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);
  const { pid: _pid, setRefetchHeader } = useContext(UserContext);

  const result = useQuery({
    queryID: `group|${groupId}`,
    request: getGroup,
    queryVariables: { id: groupId },
    forcedFilters: {},
    skip: !groupId,
  });

  const { data: currentGroup, isLoading, refetchQuery } = result || {};
  const {
    pid,
    name,
    description,
    userIds: _userIds = "",
    defaultCustomer,
    productList,
  } = currentGroup || {};

  const {
    data: _users,
    isLoading: usersLoading,
    refetchQuery: refetchUserQuery,
    setData: setUserData,
  } = useQuery({
    queryID: "users",
    request: getUsers,
    queryVariables: { pid },
    attributes: ["firstName", "id", "lastName", "pid", "username", "groupIds"],
  });

  const { rows: users = [], count } = _users || {};

  const platforms = useQuery({
    queryID: "platforms",
    request: getPlatforms,
    forcedFilters: {},
    skip: isLoading || usersLoading,
  });

  const platformOptions = useMemo(() => {
    const { rows: _platforms = [] } = platforms?.data || {};
    return _platforms.map(({ id, name }) => ({ value: id, label: name }));
  }, [platforms]);

  const productLists = useQuery({
    queryID: "productLists",
    request: getProductLists,
    queryVariables: { pid: pid || _pid },
    forcedFilters: {},
    skip: isLoading || usersLoading,
  });

  const prodListOptions = useMemo(() => {
    const { rows: _productLists = [] } = productLists?.data || {};
    return _productLists.map(({ id, name }) => ({ value: id, label: name }));
  }, [productLists]);

  const userIds = useMemo(
    () => (_userIds ? JSON.parse(_userIds) : []),
    [_userIds]
  );

  const { data, isLoading: customersLoading } = useQuery({
    request: getCustomers,
    skip: !pid,
    queryVariables: { pid, hasERP: true },
    forcedFilters: {},
  });

  const { rows: customers = [] } = data || {};

  const customerOptions = useMemo(() => {
    return !customers
      ? []
      : [
          { label: "None", value: null },
          ...customers.map((row) => {
            const { billingAddress, customer } = row || {};
            const { id, erpId, companyName } = customer || {};

            const { Address, Address2, City, State, Zip } =
              billingAddress || {};

            let label = `${companyName || erpId || "—"}`;
            if (Address || Address2) {
              if (Address) label += `, ${Address}`;
              if (Address2) {
                label += ` ${Address2}`;
              }
              if (City) {
                label += `, ${City}`;
                if (State) {
                  label += ` ${State}`;
                  if (Zip) {
                    label += `, ${Zip}`;
                  }
                }
              }
            }

            return { label, value: id };
          }),
        ];
  }, [customers]);

  const assignUser = useCallback(
    async ({ id, setTo }) => {
      const selectedUser = _.find(users, ({ id: uid }) => uid === id);
      const {
        groupIds: gids,
        firstName,
        lastName,
        username,
      } = selectedUser || {};
      const _name =
        firstName && lastName ? `${firstName} ${lastName}` : username;
      const groupIds = gids ? JSON.parse(gids) : [];
      try {
        if (setTo === false) {
          _.remove(userIds, (uid) => uid === id);
          _.remove(groupIds, (gid) => gid === groupId);

          await updateUser({
            id,
            input: { groupIds: JSON.stringify(groupIds), setTo },
          });
          await r.updateGroup({
            id: groupId,
            input: { userIds: JSON.stringify(userIds) },
          });
          showSuccessAlert(
            `Successfully removed ${_name} from the group [${name}].`
          );
          setRefetchHeader(true);
          return;
        } else {
          await updateUser({
            id,
            input: { groupIds: JSON.stringify([...groupIds, groupId]), setTo },
          });
          await r.updateGroup({ id: groupId, input: { userId: id, setTo } });
          showSuccessAlert(
            `Successfully added  ${_name} to the group [${name}].`
          );
          setRefetchHeader(true);
          return;
        }
      } catch (error) {
        showFailureAlert(`Failed to add ${_name} to the group [${name}].`);
      }
    },
    [
      users,
      setRefetchHeader,
      userIds,
      groupId,
      showSuccessAlert,
      name,
      showFailureAlert,
    ]
  );

  const columns = useMemo(
    () => [
      {
        id: "id",
        data: "id",
        header: "User ID",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "username",
        data: ({ username }) => <Body ellipsis>{username || "—"}</Body>,
        header: "Email",
        size: { md: 3, sm: 3, xs: 5 },
      },
      {
        id: "firstName",
        data: ({ firstName }) => (
          <Body center ellipsis>
            {firstName || "—"}
          </Body>
        ),
        header: "First Name",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "lastName",
        data: ({ lastName }) => (
          <Body center ellipsis>
            {lastName || "—"}
          </Body>
        ),
        header: "Last Name",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "groupIds",
        display: (props) => {
          const active = !!_.find(
            userIds,
            (userId) => userId === props.cell.row.original.id
          );
          return <Indicator active={active} id={props.cell.row.original.id} />;
        },
        header: "Assigned",
        sort: false,
        size: { lg: 1, md: 2, sm: 2, xs: 3 },
      },

      {
        display: (props) => {
          const active = !!_.find(
            userIds,
            (userId) => userId === props.cell.row.original.id
          );
          const uid = props.cell.row.original.id;
          return (
            <ButtonsWrapper id="actions">
              <Button
                size="small"
                color={theme.success}
                reverse
                disabled={active}
                iconName="faPlus"
                onClick={() => assignUser({ id: uid, setTo: true })}
              />
              <Button
                size="small"
                color={theme.danger}
                reverse
                disabled={!active}
                iconName="faMinus"
                onClick={() => assignUser({ id: uid, setTo: false })}
              />
            </ButtonsWrapper>
          );
        },
        header: "Actions",
        sort: false,
        size: { lg: 2, md: 2, sm: 2, xs: 2 },
      },
    ],
    [userIds, theme.success, theme.danger, assignUser]
  );

  const initialValues = useMemo(
    () => ({
      pid: pid || _pid,
      name,
      description,
      defaultCustomer,
      productList,
    }),
    [pid, _pid, name, description, defaultCustomer, productList]
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        if (groupId === "create") {
          const result = await r.createGroup({ input: values });
          navigate(`/Group/${result?.id}`);

          showSuccessAlert(`Successfully created Group: ${result?.id}`);
        } else {
          await r.updateGroup({
            id: groupId,
            input: values,
          });

          showSuccessAlert(`Successfully updated Group: ${groupId}`);
        }
        refetchQuery("groups");
      } catch (error) {
        console.error(error);
        showFailureAlert("Failed to create group.");
      }
    },
    [groupId, refetchQuery, navigate, showSuccessAlert, showFailureAlert]
  );

  const onDelete = useCallback(async () => {
    await r.deleteGroup({ id: groupId });
    showSuccessAlert(`Succesfully Deleted Group: ${name || groupId}`);
    await refetchQuery("groups");
    navigate(`/Groups/${pid}`);
  }, [groupId, showSuccessAlert, name, refetchQuery, navigate, pid]);

  return (
    <PageWrapper>
      <Formik
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {(formProps) => {
          const { values } = formProps;
          return (
            <>
              <HeadingSection
                my="1rem"
                hideCreate
                backButtonLink={() => navigate(`/Groups/${pid || _pid}`)}
                title={`${groupId === "create" ? "Create" : "Edit"} Group`}
              />
              <Form style={{ width: "100%" }}>
                <Field
                  required
                  labelText="Assign to Platform"
                  name="pid"
                  as="select"
                >
                  <option key="default" value="">
                    Select a Platform
                  </option>
                  {platformOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Field>
                <Field labelText="Name" name="name" />

                <Field
                  as="select"
                  labelText="Default Customer"
                  name="defaultCustomer"
                  isLoading={customersLoading}
                  description="Users assigned to this Group will have new quotes automatically generated with the selected Customer."
                >
                  {customerOptions.map(({ label, value }) => (
                    <option id={value} key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Field>

                <Field
                  labelText="Description"
                  as="textarea"
                  name="description"
                />

                <Field
                  required
                  labelText="Assign to Product List"
                  name="productList"
                  as="select"
                >
                  <option key="default" value="">
                    Select a Product List
                  </option>
                  {prodListOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Field>

                <FormButtonCluster
                  onDelete={onDelete}
                  onCancel={() => navigate(`/Groups/${pid}`)}
                  hideDelete={groupId === "create"}
                  recordAction={groupId === "create" ? "create" : "edit"}
                  deleteMessage={`Are you sure you want to delete ${values.name}?`}
                />

                {groupId !== "create" && (
                  <Table
                    createButtonText="Create User"
                    createButtonIcon="faUserPlus"
                    createLink="/Users/create"
                    title="Assigned Users"
                    hideRefetch={false}
                    refetchData={() => {
                      setUserData({ data: [] });
                      refetchUserQuery(`group|${groupId}`);
                    }}
                    subtitle="Use this table control to assign or unassign users from this group. Assigned Users will be able to select this group from the Groups dropdown."
                    data={users}
                    hideToggle
                    columns={columns}
                    hideBack
                    hideCreate
                    // onRowClick={onRowClick}
                    backButtonLink="/Administration"
                    // selections={selections}
                    isLoading={usersLoading || users.length < 1}
                    totalCount={count}
                  />
                )}

                {/* <Body>Assigned Users</Body>
                <Flex style={{ gap: "1rem" }} flexWrap="wrap">
                  {userOptions}
                </Flex> */}
              </Form>
            </>
          );
        }}
      </Formik>
    </PageWrapper>
  );
};

export default GroupForm;
